import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  concatMap,
  debounceTime,
  filter,
  map,
  merge,
  mergeMap,
  MonoTypeOperatorFunction,
  Observable,
  of,
  startWith,
  switchMap,
  take,
} from 'rxjs';

import { EffectsBase, withCacheGate } from '@ps/core-data-access';
import { CACHE_TTL, isCacheStale, markCached, Undefinable } from '@ps/utils';
import {
  mapSurveyLocalization,
  Qualification,
  Survey,
  SurveyInfo,
  SurveyKind,
} from '@ps/survey-utils';

import { QualificationsService } from '../../services/qualifications/qualifications.service';
import { SurveyQuestionService } from '../../services/survey-question/survey-question.service';
import { LocationConditionsService } from '../../services/location-conditions/location-conditions.service';
import {
  isSurveyQuestionsCacheValid,
  locationConditionsCacheKey,
  surveyAdvancedQualificationCacheKey,
} from '../../utils';
import { CachedEntry } from '../../utils/cache/cache.helper';
import {
  SurveyCreationCoreApiActions,
  SurveyCreationCoreChildrenApiActions,
} from '../core/survey-creation-core.actions';
import { distinctLocalesOf } from './utils/distinct-locales.helper';
import {
  SurveyCreationReferenceActions,
  SurveyCreationReferenceApiActions,
} from './survey-creation-reference.actions';
import {
  selectLocationConditionsRecord,
  selectQualificationsRecord,
  selectQuestionCategoriesCachedAt,
  selectQuestionsRecord,
} from './survey-creation-reference.selectors';

type LoadQuestionsRequest = ReturnType<
  typeof SurveyCreationReferenceActions.loadQuestions
>;

const QUALIFICATIONS_CACHE_TTL_MS = CACHE_TTL.DEFAULT;
const LOCATION_CONDITIONS_CACHE_TTL_MS = CACHE_TTL.TWO_HOUR;

function isCatalogFresh(
  entry: Undefinable<CachedEntry<Qualification[]>>,
): boolean {
  return (
    !!entry &&
    entry.data.length > 0 &&
    !isCacheStale(entry.cachedAt, QUALIFICATIONS_CACHE_TTL_MS)
  );
}

@Injectable()
export class SurveyCreationReferenceEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);

  private readonly _qualificationsService = inject(QualificationsService);
  private readonly _questionService = inject(SurveyQuestionService);
  private readonly _locationConditionsService = inject(
    LocationConditionsService,
  );

  // A family's other locales aren't opened yet, but their targeting is built
  // from their catalogs — so every child's locale loads with the family.
  public readonly loadFamilyQualifications$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess),
      mergeMap(({ surveys }) => [
        ...distinctLocalesOf(surveys).map((locale) =>
          SurveyCreationReferenceActions.loadQualifications(locale),
        ),
        ...this._appliedAdvancedQuestionsActions(surveys),
      ]),
    ),
  );

  // A standalone survey never loads a family.
  public readonly loadStandaloneAdvancedQuestions$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess),
      filter(({ survey }) => SurveyKind.childFilter(survey).isNone()),
      mergeMap(({ survey }) => this._appliedAdvancedQuestionsActions([survey])),
    ),
  );

  // The cache is read when the request is about to run, not when the action
  // arrives, so a second request for a locale already in flight is skipped.
  public readonly loadQualifications$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationReferenceActions.loadQualifications),
      concatMap(({ countryCode, languageShortCode }) =>
        this._store.select(selectQualificationsRecord).pipe(
          take(1),
          filter(
            (qualificationsRecord) =>
              !isCatalogFresh(
                qualificationsRecord[
                  mapSurveyLocalization(countryCode, languageShortCode)
                ],
              ),
          ),
          switchMap(() =>
            this._qualificationsService
              .getQualifications(countryCode, languageShortCode)
              .pipe(
                map((qualifications) =>
                  SurveyCreationReferenceApiActions.loadQualificationsSuccess({
                    countryCode,
                    languageShortCode,
                    qualifications,
                    cachedAt: markCached(),
                  }),
                ),
                this.psCatchError((err) =>
                  of(
                    SurveyCreationReferenceApiActions.loadQualificationsFailure(
                      {
                        countryCode,
                        languageShortCode,
                        error: err.message || 'Failed to load qualifications',
                      },
                    ),
                  ),
                ),
              ),
          ),
        ),
      ),
    ),
  );

  public readonly loadQuestionCategories$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationReferenceActions.loadQuestionCategories),
      withCacheGate(
        this._store.select(selectQuestionCategoriesCachedAt),
        CACHE_TTL.DEFAULT,
      ),
      switchMap(() =>
        this._questionService.categories().pipe(
          map((categories) =>
            SurveyCreationReferenceApiActions.loadQuestionCategoriesSuccess({
              categories,
              cachedAt: markCached(),
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                SurveyCreationReferenceApiActions.loadQuestionCategoriesFailure(
                  {
                    error: err.message || 'Failed to load question categories',
                  },
                ),
              ),
            {
              notify: true,
              customMessage:
                'An error occurred while loading categories. Please try again.',
            },
          ),
        ),
      ),
    ),
  );

  // Search: debounced, newest wins. No search: immediate, parallel.
  public readonly loadQuestions$ = createEffect(() => {
    const requests$ = this._actions$.pipe(
      ofType(SurveyCreationReferenceActions.loadQuestions),
    );

    return merge(
      requests$.pipe(
        filter(({ search }) => !!search),
        debounceTime(500),
        this._uncachedQuestions(),
        switchMap((request) => this._loadQuestions$(request)),
      ),
      requests$.pipe(
        filter(({ search }) => !search),
        this._uncachedQuestions(),
        mergeMap((request) => this._loadQuestions$(request)),
      ),
    );
  });

  public readonly loadDependencyParentQuestion$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationReferenceActions.loadDependencyParentQuestion),
      concatLatestFrom(() => this._store.select(selectQuestionsRecord)),
      filter(
        ([{ parentQualificationId, locale }, questionsRecord]) =>
          !isSurveyQuestionsCacheValid(
            surveyAdvancedQualificationCacheKey(
              String(parentQualificationId),
              [],
              locale,
            ),
            questionsRecord,
          ),
      ),
      mergeMap(([{ parentQualificationId, locale }]) => {
        const search = String(parentQualificationId);
        const key = surveyAdvancedQualificationCacheKey(search, [], locale);

        return this._questionService.getQuestions(search, [], locale).pipe(
          map((questions) =>
            SurveyCreationReferenceApiActions.loadQuestionsSuccess({
              key,
              questions,
              cachedAt: markCached(),
            }),
          ),
          this.psCatchError((err) =>
            of(
              SurveyCreationReferenceApiActions.loadQuestionsFailure({
                key,
                error:
                  err.message || 'Failed to load the parent of this question',
              }),
            ),
          ),
          startWith(
            SurveyCreationReferenceApiActions.loadQuestionsStarted({ key }),
          ),
        );
      }),
    ),
  );

  /**
   * Loads the full option list for a location qualification once per
   * (dbQualificationCode, locale) and caches it for
   * CACHE_TTL.TWO_HOUR — location values (CBSA/CSA/County/DMA names) are
   * static reference data, so there's no reason to keep re-fetching within a
   * session. Unlike the old per-keystroke search this superseded, the buyer's
   * typed text never reaches this effect (or the server) at all — it's used
   * purely for client-side filtering (see
   * SearchLocationQualificationContentComponent) against whatever this
   * effect loads. The cache gate treats a fresh empty result as valid too
   * (mirrors isSurveyQuestionsCacheValid — "no options" is itself a complete
   * answer, not a reason to keep asking). `switchMap` still cancels an
   * overlapping in-flight load (e.g. rapid tab switching).
   */
  public readonly loadLocationConditions$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationReferenceActions.loadLocationConditions),
      concatLatestFrom(() =>
        this._store.select(selectLocationConditionsRecord),
      ),
      filter(([{ dbQualificationCode, locale }, locationConditionsRecord]) => {
        const key = locationConditionsCacheKey(dbQualificationCode, locale);
        const entry = locationConditionsRecord[key];
        return !(
          entry &&
          !isCacheStale(entry.cachedAt, LOCATION_CONDITIONS_CACHE_TTL_MS)
        );
      }),
      switchMap(([{ dbQualificationCode, locale }]) => {
        const key = locationConditionsCacheKey(dbQualificationCode, locale);

        return this._locationConditionsService
          .getAll(dbQualificationCode, locale)
          .pipe(
            map((conditions) =>
              SurveyCreationReferenceApiActions.loadLocationConditionsSuccess({
                key,
                conditions,
                cachedAt: markCached(),
              }),
            ),
            this.psCatchError(
              (err) =>
                of(
                  SurveyCreationReferenceApiActions.loadLocationConditionsFailure(
                    {
                      key,
                      error:
                        err.message || 'Failed to load location conditions',
                    },
                  ),
                ),
              {
                notify: true,
                customMessage:
                  'An error occurred while loading options. Please try again.',
              },
            ),
            startWith(
              SurveyCreationReferenceApiActions.loadLocationConditionsStarted({
                key,
              }),
            ),
          );
      }),
    ),
  );

  private _appliedAdvancedQuestionsActions(
    surveys: readonly Survey[],
  ): Action[] {
    const locales = distinctLocalesOf(
      surveys.filter((survey) =>
        new SurveyInfo(survey).hasAdvancedQualifications(),
      ),
    );

    return locales.length
      ? [
          SurveyCreationReferenceActions.loadQuestionCategories(),
          ...locales.map(({ countryCode, languageShortCode }) =>
            SurveyCreationReferenceActions.loadQuestions({
              search: '',
              categoryIds: [],
              locale: mapSurveyLocalization(countryCode, languageShortCode),
              loadingScope: 'none',
            }),
          ),
        ]
      : [];
  }

  private _uncachedQuestions(): MonoTypeOperatorFunction<LoadQuestionsRequest> {
    return (requests$) =>
      requests$.pipe(
        concatLatestFrom(() => this._store.select(selectQuestionsRecord)),
        filter(
          ([{ search, categoryIds, locale }, questionsRecord]) =>
            !isSurveyQuestionsCacheValid(
              surveyAdvancedQualificationCacheKey(search, categoryIds, locale),
              questionsRecord,
            ),
        ),
        map(([request]) => request),
      );
  }

  private _loadQuestions$({
    search,
    categoryIds,
    locale,
    loadingScope = 'drawer',
  }: LoadQuestionsRequest): Observable<Action> {
    const key = surveyAdvancedQualificationCacheKey(
      search,
      categoryIds,
      locale,
    );

    const result$ = this._questionService
      .getQuestions(search, categoryIds, locale, loadingScope)
      .pipe(
        map((questions) =>
          SurveyCreationReferenceApiActions.loadQuestionsSuccess({
            key,
            questions,
            cachedAt: markCached(),
          }),
        ),
        this.psCatchError((err) =>
          of(
            SurveyCreationReferenceApiActions.loadQuestionsFailure({
              key,
              error: err.message || 'Failed to load questions',
            }),
          ),
        ),
      );

    // A silent load leaves the drawer's loading key alone.
    return loadingScope === 'none'
      ? result$
      : result$.pipe(
          startWith(
            SurveyCreationReferenceApiActions.loadQuestionsStarted({ key }),
          ),
        );
  }
}
